<template>
  <div class="supplement-list-container">
    <div v-if="showSearch" class="field mb-3">
      <div class="control has-icons-left is-flex-grow-1">
        <span class="icon is-left">
          <i class="fas fa-search"></i>
        </span>
        <input
          v-model="searchQuery"
          class="input is-rounded"
          type="search"
          placeholder="Search supplements..."
          inputmode="search"
          @input="handleSearchInput"
        />
      </div>
    </div>

    <div v-if="!showSearch && showArchiveFilter" class="filter-archived">
      <label class="checkbox">
        <input
          type="checkbox"
          :checked="archivedFilter === 'all'"
          @change="handleIncludeArchivedChange"
        />
        Include archived
      </label>
    </div>

    <div
      v-if="visibleLabels.length === 0 && loaded && hasActiveFilters"
      class="has-text-centered is-size-5 is-italic mt-6"
    >
      <p>No supplement labels matched your search criteria.</p>
      <p><a @click.prevent="handleClearSearch">Clear search</a></p>
    </div>

    <div
      v-else-if="visibleLabels.length === 0 && loaded && !hasActiveFilters"
      class="has-text-centered empty-state"
    >
      <h2 class="subtitle">Create your first FDA Supplement Label</h2>
      <div class="is-flex is-justify-content-center mb-5">
        <a :href="createPath" class="button is-success">
          <span class="icon-text">
            <span class="icon">
              <i class="fa fa-plus"></i>
            </span>
            <span>Create a supplement label</span>
          </span>
        </a>
      </div>

      <div class="columns is-centered mt-4">
        <div class="column is-two-thirds">
          <figure class="youtube-video image is-16by9">
            <iframe
              class="has-ratio"
              src="https://www.youtube.com/embed/8vo62AYY1kg?wmode=transparent&rel=0&autoplay=0"
              frameborder="0"
              allowfullscreen
            ></iframe>
          </figure>
        </div>
      </div>
    </div>

    <table
      v-else-if="visibleLabels.length > 0"
      class="table is-fullwidth is-striped supplement-list"
    >
      <thead>
        <tr>
          <th class="col-name" width="1">
            <a href="#" @click.prevent="handleSortClick('name')">
              Name
              <span v-if="sortBy === 'name'" class="icon is-small">
                <i :class="sortDirection === 'asc' ? 'fa fa-sort-up' : 'fa fa-sort-down'"></i>
              </span>
            </a>
          </th>
          <th class="col-modified is-hidden-touch is-hidden-desktop-only">
            <a href="#" @click.prevent="handleSortClick('updated_at')">Modified</a>
          </th>
          <th></th>
        </tr>
      </thead>

      <tbody>
        <tr v-for="label in visibleLabels" :key="label.id">
          <td class="col-name">
            <a :href="editPath(label.id)">{{ label.name }}</a>
            <span v-if="label.archived" class="chip archived-chip">Archived</span>
            <span class="updated-at-mobile is-italic is-size-7">
              updated {{ dateDisplay(label.updated_at) }}
            </span>
          </td>
          <td class="col-modified is-hidden-touch is-hidden-desktop-only">
            {{ dateDisplay(label.updated_at) }}
          </td>
          <td class="col-actions no-print">
            <div class="is-flex gap-2 is-justify-content-flex-end">
              <SupplementActionsButton
                :label="label"
                :enable-archiving="true"
                @copy="copyLabel"
                @archive="toggleArchive"
                @delete="handleDelete"
              />
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <PagerWidget
      v-if="totalEntries > perPage"
      v-model="currentPage"
      :total-entries="totalEntries"
      :per-page="perPage"
      @update:model-value="handlePageChange"
    />

    <ConfirmDialog
      v-model="showDeleteDialog"
      confirm-text="Delete"
      cancel-text="Cancel"
      confirm-class="is-danger"
      @confirm="confirmDelete"
      @cancel="cancelDelete"
    >
      Delete this supplement label? This cannot be reversed.
      <a href="#" class="has-text-weight-bold" @click.prevent="archiveInsteadOfDelete">
        Archive instead
      </a>
      if you might need to recover it later.
    </ConfirmDialog>
  </div>
</template>

<script setup>import { computed as _computed } from 'vue';

  import PagerWidget from "./pager-widget.vue";
  import ConfirmDialog from "./shared/confirm-dialog.vue";
  import SupplementActionsButton from "./supplement-actions-button.vue";
  import { editSupplementLabelPath, supplementLabelPath } from "~/routes";
  import { parseISO, formatRelative } from "date-fns";
  import { storeToRefs } from "pinia";
  import { debounce } from "lodash-es";
  import { ref } from "vue";
  import { Flash } from "~/jose_modern/flash";
  import { Confirm } from "~/jose_modern/confirm";

  const props = defineProps({
    supplementSearchStore: { type: Object, required: true },
    showSearch: { type: Boolean, default: false },
    showArchiveFilter: { type: Boolean, default: false },
    userId: { type: Number, default: null },
  });

  const { visibleLabels, totalEntries, currentPage, perPage, sortBy, sortDirection, loaded } =
    storeToRefs(props.supplementSearchStore);

  const searchQuery = ref(props.supplementSearchStore.q);
  const archivedFilter = ref(props.supplementSearchStore.archived);
  const copyingId = ref(null);
  const togglingArchiveId = ref(null);
  const showDeleteDialog = ref(false);
  const labelToDelete = ref(null);

  const createPath = _computed(() =>
    props.userId ? `/supplement_labels/new?user_id=${props.userId}` : "/supplement_labels/new"
  );

  const hasActiveFilters = _computed(() => {
    return searchQuery.value !== "" || archivedFilter.value !== null;
  });

  function dateDisplay(updatedAt) {
    return formatRelative(parseISO(updatedAt), new Date());
  }

  function editPath(id) {
    return editSupplementLabelPath({ id });
  }

  function deletePath(id) {
    return supplementLabelPath({ id });
  }

  function handleClearSearch() {
    searchQuery.value = "";
    props.supplementSearchStore.resetSearch();
  }

  function handleSortClick(field) {
    props.supplementSearchStore.toggleSort(field);
  }

  function handlePageChange(page) {
    props.supplementSearchStore.loadPage(page);
  }

  const debouncedSearch = debounce((q) => {
    props.supplementSearchStore.setQueryFilter(q);
  }, 300);

  function handleSearchInput() {
    debouncedSearch(searchQuery.value);
  }

  async function copyLabel(label) {
    if (copyingId.value) return;
    copyingId.value = label.id;

    try {
      const response = await fetch(`/supplement_labels/${label.id}/copy`, {
        method: "POST",
        headers: {
          Accept: "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        Flash.success(`Created "${data.name}"`);
        window.location.href = editSupplementLabelPath({ id: data.id });
      } else {
        const data = await response.json();
        Flash.error(data.errors?.join(", ") || "Failed to copy label");
      }
    } catch (e) {
      Flash.error("Failed to copy label");
    } finally {
      copyingId.value = null;
    }
  }

  function handleIncludeArchivedChange(event) {
    const include = event.target.checked;
    const newValue = include ? "all" : false;
    archivedFilter.value = newValue;
    props.supplementSearchStore.setArchivedFilter(newValue);
  }

  function handleDelete(label) {
    labelToDelete.value = label;
    showDeleteDialog.value = true;
  }

  function archiveInsteadOfDelete() {
    showDeleteDialog.value = false;
    if (labelToDelete.value) {
      toggleArchiveNoConfirm(labelToDelete.value);
      labelToDelete.value = null;
    }
  }

  function confirmDelete() {
    if (!labelToDelete.value) return;

    const form = document.createElement("form");
    form.method = "POST";
    form.action = deletePath(labelToDelete.value.id);

    const methodInput = document.createElement("input");
    methodInput.type = "hidden";
    methodInput.name = "_method";
    methodInput.value = "delete";
    form.appendChild(methodInput);

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
    if (csrfToken) {
      const csrfInput = document.createElement("input");
      csrfInput.type = "hidden";
      csrfInput.name = "authenticity_token";
      csrfInput.value = csrfToken;
      form.appendChild(csrfInput);
    }

    document.body.appendChild(form);
    form.submit();

    labelToDelete.value = null;
  }

  function cancelDelete() {
    labelToDelete.value = null;
  }

  async function toggleArchive(label) {
    if (togglingArchiveId.value) return;

    const confirmed = await Confirm.confirm(
      label.archived ? "Restore this label?" : "Archive this label?",
      {
        confirm: label.archived ? "Restore" : "Archive",
        cancel: "Cancel",
        confirmClass: label.archived ? "is-success" : "is-warning",
      }
    );

    if (!confirmed) return;

    togglingArchiveId.value = label.id;

    try {
      const response = await fetch(`/supplement_labels/${label.id}/toggle_archive`, {
        method: "PUT",
        headers: {
          Accept: "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        label.archived = data.archived;
        Flash.success(data.archived ? "Label archived" : "Label unarchived");
        props.supplementSearchStore.loadPage(props.supplementSearchStore.currentPage);
      } else {
        const data = await response.json();
        Flash.error(data.errors?.join(", ") || "Failed to toggle archive status");
      }
    } catch (e) {
      Flash.error("Failed to toggle archive status");
    } finally {
      togglingArchiveId.value = null;
    }
  }

  async function toggleArchiveNoConfirm(label) {
    if (togglingArchiveId.value) return;

    togglingArchiveId.value = label.id;

    try {
      const response = await fetch(`/supplement_labels/${label.id}/toggle_archive`, {
        method: "PUT",
        headers: {
          Accept: "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        label.archived = data.archived;
        Flash.success(data.archived ? "Label archived" : "Label unarchived");
        props.supplementSearchStore.loadPage(props.supplementSearchStore.currentPage);
      } else {
        const data = await response.json();
        Flash.error(data.errors?.join(", ") || "Failed to toggle archive status");
      }
    } catch (e) {
      Flash.error("Failed to toggle archive status");
    } finally {
      togglingArchiveId.value = null;
    }
  }
</script>

<style scoped lang="scss">
  @import "~jose_modern/css";

  .supplement-list-container {
    display: flex;
    flex-direction: column;
  }

  .col-name {
    min-width: 200px;
  }

  .col-modified {
    white-space: nowrap;
  }

  .filter-archived {
    position: relative;

    & > label {
      position: absolute;
      top: 0.5rem;
      right: 0;

      @include until($tablet) {
        top: 0.625rem;
      }
    }
  }

  .updated-at-mobile {
    display: none;
  }

  table {
    tbody tr td {
      vertical-align: middle;
    }
  }

  @include desktop-only {
    tbody tr {
      border-top: 1px solid $grey-lighter;
    }
  }

  @include tablet-only {
    tbody tr {
      border-top: 1px solid $grey-lighter;
    }

    th.col-name {
      min-width: 50ch;
    }
  }

  @include until($tablet) {
    .table thead th {
      border: none;
      padding-top: 0;
    }
  }

  @include until($widescreen) {
    table {
      & > thead > tr {
        display: flex;
        flex-direction: row;

        & > th {
          flex-grow: 1;
        }
      }

      & > tbody {
        & > tr {
          display: flex;
          flex-direction: row;
          flex-wrap: wrap;
          align-items: center;

          & > td {
            border: none;
            flex-direction: row;
          }

          & > td.col-name {
            display: flex;
            flex-basis: 50%;
            flex-grow: 1;
            flex-direction: column;
          }

          & > td.col-actions {
            display: flex;
            order: 2;
          }
        }
      }
    }
  }

  @include mobile {
    table {
      margin-top: 0.5rem;

      & > thead {
        border: none;
      }

      & > th {
        border: none;
      }

      & > tbody {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;

        & > tr {
          border: 1px solid $grey-light;
          border-radius: $radius-large;
          background-color: transparent !important;
        }
      }
    }

    .updated-at-mobile {
      display: block;
    }
  }

  .empty-state {
    margin-top: 2rem;
  }
</style>
